/**
 * Publisher reads and the release lifecycle.
 *
 * Coordinates what a release touches: the PUBLISHER store (records), the SONGS store
 * (what is being released), ENTITLEMENTS (the plan), and the UI store (toasts). Same
 * division of labour the other hooks follow — the stores never reach into each other.
 *
 * ============================================================================
 * PROTOTYPE SUBMISSION RECORDS RECEIPT, NOT PROVIDER DELIVERY.
 *
 * `submit()` places a release in the local review queue in mock mode. It never invents
 * an external id or provider status. In backend mode the API response remains the only
 * authority for LabelGrid acceptance and delivery facts.
 * ============================================================================
 */
import { useCallback, useEffect, useMemo } from 'react';
import {
  DistributionUnavailableError,
  describePublisherSongBlocker,
  publisherApi,
  PUBLISHING_FEE,
  SubmissionRefusedError,
} from '../api/publisherApi';
import { USE_MOCK_API } from '../config';
import { openExternalUrl } from '../lib/externalNavigation';
import { isUploadedSong } from '../lib/license';
import { buildPublisherDashboard } from '../lib/publisherDashboard';
import { findPublisherIssues, isReleaseComplete } from '../lib/publisherValidation';
import {
  countReleases,
  selectMostRecentDraftProgress,
  selectReleaseById,
  usePublisherStore,
  type PendingDashboardDeletion,
  type PublisherLastVisibleSurface,
} from '../stores/publisherStore';
import { useEntitlementsStore } from '../stores/entitlementsStore';
import { usePublisherCreditsStore } from '../stores/publisherCreditsStore';
import { useSongsStore } from '../stores/songsStore';
import { useUiStore } from '../stores/uiStore';
import type { PublisherRelease, PublisherReleaseType } from '../types/domain';

/**
 * What a submission attempt produced.
 *
 * A discriminated result rather than a thrown error, because every outcome here is one
 * the UI has to render differently and none of them is exceptional: a refusal routes the
 * user to a step, and unavailability shows the blockers. `ok: true` is unreachable in
 * this build and is kept in the type because it is what the backend will return — the
 * caller's success branch should exist and be correct before it can run.
 */
export type SubmitResult =
  | { ok: true; release: PublisherRelease }
  | { ok: false; kind: 'refused'; reason: SubmissionRefusedError['reason']; message: string }
  | {
      ok: false;
      kind: 'unavailable';
      message: string;
      blockers: DistributionUnavailableError['blockers'];
    };

export function usePublisher() {
  // One field per selector — Zustand v5 compares with Object.is.
  const releases = usePublisherStore((s) => s.releases);
  const payments = usePublisherStore((s) => s.payments);
  const events = usePublisherStore((s) => s.events);
  const platforms = usePublisherStore((s) => s.platforms);
  const wizardProgress = usePublisherStore((s) => s.wizardProgress);
  const lastVisibleSurface = usePublisherStore((s) => s.lastVisibleSurface);
  const pendingDashboardDeletions = usePublisherStore((s) => s.pendingDashboardDeletions);
  const load = usePublisherStore((s) => s.load);
  const submitting = usePublisherStore((s) => s.submitting);
  const createReleaseRaw = usePublisherStore((s) => s.createRelease);
  const updateRelease = usePublisherStore((s) => s.updateRelease);
  const scheduleReleaseDeletionRaw = usePublisherStore((s) => s.scheduleReleaseDeletion);
  const undoReleaseDeletionRaw = usePublisherStore((s) => s.undoReleaseDeletion);
  const purgeExpiredReleaseDeletionsRaw = usePublisherStore((s) => s.purgeExpiredReleaseDeletions);
  const setStatus = usePublisherStore((s) => s.setStatus);
  const addEvent = usePublisherStore((s) => s.addEvent);
  const ensurePayment = usePublisherStore((s) => s.ensurePayment);
  const updatePayment = usePublisherStore((s) => s.updatePayment);
  const simulateReleasedRelease = usePublisherStore((s) => s.simulateReleasedRelease);
  const platformRowsForRaw = usePublisherStore((s) => s.platformRowsFor);
  const setWizardProgress = usePublisherStore((s) => s.setWizardProgress);
  const clearWizardProgress = usePublisherStore((s) => s.clearWizardProgress);
  const setLastVisibleSurface = usePublisherStore((s) => s.setLastVisibleSurface);
  const beginSubmit = usePublisherStore((s) => s.beginSubmit);
  const endSubmit = usePublisherStore((s) => s.endSubmit);
  const loadPublisher = usePublisherStore((s) => s.loadPublisher);

  const songs = useSongsStore((s) => s.songs);
  /*
    WHETHER `songs` IS THE WHOLE LIBRARY, which is what tells a release's completeness check
    "this id is gone" apart from "this id has not arrived". See publisherValidation's
    `songsLoaded` parameter for what goes wrong without it; this is the one place the app
    answers the question, because `issuesFor` and `isComplete` below are the only two ways in.

    Runtime now starts from an unresolved library and hydrates from `GET /songs`, so `idle`
    means "we have not asked yet", not "the answer is already in the store". The one
    exception is the default Vitest fixture path, where `USE_MOCK_API` keeps the seeded
    library and no boot read runs.

    `loading` and `error` are both false, INCLUDING a refresh that still holds data.
    `songs.length > 0` looks like a better proxy and is not: a failed refresh can still leave
    stale songs in memory, and checking a release against them would produce exactly the false
    "remove or replace it" this guard exists to stop. The cost is that during a re-read of an
    already-loaded library a genuinely deleted track is reported as unknown rather than as
    deleted for a few hundred milliseconds. Both block; only the sentence differs, and it errs
    toward not accusing.
  */
  const songsLoadStatus = useSongsStore((s) => s.load.status);
  const songsLoaded = songsLoadStatus === 'ready' || (USE_MOCK_API && songsLoadStatus === 'idle');
  const entitled = useEntitlementsStore((s) => s.publisher);
  const unlimited = useEntitlementsStore((s) => s.unlimited);
  const showToast = useUiStore((s) => s.showToast);

  useEffect(() => {
    purgeExpiredReleaseDeletionsRaw();
  }, [purgeExpiredReleaseDeletionsRaw]);

  const visibleReleaseIds = useMemo(() => {
    const hidden = new Set(Object.keys(pendingDashboardDeletions));
    return new Set(
      releases.filter((release) => !hidden.has(release.id)).map((release) => release.id)
    );
  }, [releases, pendingDashboardDeletions]);

  const visibleReleases = useMemo(
    () => releases.filter((release) => visibleReleaseIds.has(release.id)),
    [releases, visibleReleaseIds]
  );
  const visiblePayments = useMemo(
    () => payments.filter((payment) => visibleReleaseIds.has(payment.releaseId)),
    [payments, visibleReleaseIds]
  );
  const visibleEvents = useMemo(
    () => events.filter((event) => visibleReleaseIds.has(event.releaseId)),
    [events, visibleReleaseIds]
  );
  const visiblePlatforms = useMemo(
    () => platforms.filter((platform) => visibleReleaseIds.has(platform.releaseId)),
    [platforms, visibleReleaseIds]
  );
  const pendingReleaseDeletions = useMemo(
    () =>
      releases
        .map((release) => {
          const pending = pendingDashboardDeletions[release.id];
          return pending ? { release, pending } : null;
        })
        .filter(
          (entry): entry is { release: PublisherRelease; pending: PendingDashboardDeletion } =>
            entry !== null
        )
        .sort((left, right) => right.pending.requestedAt.localeCompare(left.pending.requestedAt)),
    [releases, pendingDashboardDeletions]
  );

  /** Creates a draft and records that it happened. */
  const createRelease = useCallback(
    (releaseType: PublisherReleaseType) => {
      const release = createReleaseRaw({ releaseType });
      addEvent(release.id, `${releaseType} draft created.`, {
        type: 'milestone',
        detail: 'The release record is ready for metadata, artwork, lyrics, and outlet setup.',
        actionLabel: 'Resume release',
      });
      return release;
    },
    [createReleaseRaw, addEvent]
  );

  const scheduleReleaseDeletion = useCallback(
    (id: string) => {
      scheduleReleaseDeletionRaw(id);
      showToast('Release scheduled for dashboard removal');
    },
    [scheduleReleaseDeletionRaw, showToast]
  );

  const undoReleaseDeletion = useCallback(
    (id: string) => {
      undoReleaseDeletionRaw(id);
      showToast('Release restored to the dashboard');
    },
    [undoReleaseDeletionRaw, showToast]
  );

  /**
   * Attempts to pay the per-release fee.
   *
   * §27 — the payment record and its idempotency key are created once per release and
   * reused, so a retry cannot produce a second charge. The call is still made through the
   * API boundary, which refuses: there is no payment provider, and a simulated "Payment
   * Successful ✓" would be a $12 charge the user believes happened.
   *
   * `status: 'pending'` is written before the call and rolled back to `'failed'` after,
   * because that is the sequence a real provider needs — the record must exist before the
   * charge so a crash mid-flight leaves evidence rather than nothing.
   */
  const payFee = useCallback(
    async (releaseId: string): Promise<SubmitResult> => {
      const payment = ensurePayment(releaseId);
      // Already verified: nothing to do, and charging again would be the duplicate §27
      // forbids.
      if (payment.verifiedAt) {
        return {
          ok: false,
          kind: 'refused',
          reason: 'already-submitted',
          message: 'Already paid.',
        };
      }
      updatePayment(payment.id, { status: 'pending', failureReason: null });
      try {
        const checkout = await publisherApi.payPublishingFee({
          releaseId,
          paymentId: payment.id,
          idempotencyKey: payment.idempotencyKey,
        });
        updatePayment(payment.id, checkout.payment);
        if (checkout.checkoutUrl) {
          addEvent(releaseId, 'Publishing fee checkout opened.', {
            type: 'milestone',
            detail: 'The per-release payment flow opened in a new tab for this submission.',
            actionLabel: 'Open release',
          });
          openExternalUrl(checkout.checkoutUrl);
        }
        return {
          ok: false,
          kind: 'refused',
          reason: 'payment-unverified',
          message: checkout.payment.verifiedAt
            ? 'Payment verified. You can now submit.'
            : 'Awaiting verification.',
        };
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : `The $${PUBLISHING_FEE} fee could not be charged.`;
        updatePayment(payment.id, { status: 'failed', failureReason: message });
        addEvent(releaseId, 'Publishing fee could not be charged.', {
          type: 'issue',
          detail: message,
          actionLabel: 'Open release',
        });
        if (error instanceof DistributionUnavailableError) {
          return { ok: false, kind: 'unavailable', message, blockers: error.blockers };
        }
        return { ok: false, kind: 'refused', reason: 'payment-unverified', message };
      }
    },
    [ensurePayment, updatePayment, addEvent]
  );

  /**
   * Attempts to submit a release for distribution.
   *
   * THE DOUBLE-CLICK GUARD IS FIRST, and it is a store claim rather than a local flag so
   * it holds across both surfaces that can submit (§27). A refused claim returns without
   * touching anything.
   *
   * Mock mode records a local queue receipt. Backend mode delegates to the API boundary,
   * which remains the authority for provider acceptance and delivery state.
   */
  const submit = useCallback(
    async (releaseId: string): Promise<SubmitResult> => {
      const release = usePublisherStore.getState().releases.find((r) => r.id === releaseId);
      if (!release) {
        return {
          ok: false,
          kind: 'refused',
          reason: 'already-submitted',
          message: 'Release not found.',
        };
      }
      if (!useEntitlementsStore.getState().commercial) {
        return {
          ok: false,
          kind: 'refused',
          reason: 'commercial-license-required',
          message: 'A Commercial License is required before music can be released.',
        };
      }
      if (release.rightsAttested !== true) {
        return {
          ok: false,
          kind: 'refused',
          reason: 'rights-attestation-required',
          message:
            'Confirm that you have the rights and permissions required to release every track.',
        };
      }
      const currentSongs = useSongsStore.getState().songs;
      for (const songId of release.songIds) {
        const song = currentSongs.find((candidate) => candidate.id === songId);
        if (!song) continue;
        const blocker = describePublisherSongBlocker(song);
        if (blocker) {
          return {
            ok: false,
            kind: 'refused',
            reason: isUploadedSong(song) ? 'song-license-unverified' : 'no-audio',
            message: blocker,
          };
        }
      }
      if (!beginSubmit(releaseId)) {
        return {
          ok: false,
          kind: 'refused',
          reason: 'already-submitted',
          message: 'A submission for this release is already in progress.',
        };
      }
      try {
        if (USE_MOCK_API) {
          const publishingCredits = release.songIds.length;
          if (!unlimited && !usePublisherCreditsStore.getState().spend(publishingCredits)) {
            return {
              ok: false,
              kind: 'refused',
              reason: 'publisher-credits-insufficient',
              message: `This release needs ${publishingCredits} Publisher Credit${publishingCredits === 1 ? '' : 's'}. Buy or renew Publisher Credits to continue.`,
            };
          }
          const queuedRelease: PublisherRelease = {
            ...release,
            status: 'in_review',
            externalId: null,
            providerReviewStatus: null,
            providerDeliveryStatus: null,
            failureReason: null,
            updatedAt: new Date().toISOString(),
          };
          updateRelease(releaseId, queuedRelease);
          addEvent(releaseId, 'Release received for distribution.', {
            source: 'local',
            type: 'milestone',
            detail:
              'The release is in Lyrixsa’s prototype distribution queue. No provider delivery has been claimed.',
            actionLabel: 'Open release',
          });
          return { ok: true, release: queuedRelease };
        }

        const submittedRelease = await publisherApi.submitRelease({
          release,
          songs: useSongsStore.getState().songs,
          entitled: useEntitlementsStore.getState().publisher,
        });
        void publisherApi
          .getPublisherCredits()
          .then((balance) => usePublisherCreditsStore.getState().hydrate(balance))
          .catch(() => undefined);
        updateRelease(releaseId, submittedRelease);
        const providerAccepted = submittedRelease.externalId != null;
        addEvent(
          releaseId,
          providerAccepted
            ? 'Release submitted to LabelGrid.'
            : 'Release received for distribution.',
          {
            source: providerAccepted ? 'provider' : 'local',
            type: providerAccepted ? 'platform_update' : 'milestone',
            detail: providerAccepted
              ? 'The distributor accepted the submission request and returned a provider-backed release record.'
              : 'The release is in Lyrixsa’s prototype distribution queue. No provider delivery has been claimed.',
            actionLabel: 'Open release',
          }
        );
        return { ok: true, release: submittedRelease };
      } catch (error) {
        if (error instanceof SubmissionRefusedError) {
          addEvent(releaseId, `Submission refused: ${error.message}`, {
            type: 'issue',
            detail: 'The release stayed in Lyrixa because a required submission guard failed.',
            actionLabel: 'Fix issue',
          });
          return { ok: false, kind: 'refused', reason: error.reason, message: error.message };
        }
        if (error instanceof DistributionUnavailableError) {
          addEvent(releaseId, 'Submission could not be sent to the distributor.', {
            type: 'issue',
            detail: error.message,
            actionLabel: 'Open release',
          });
          return {
            ok: false,
            kind: 'unavailable',
            message: error.message,
            blockers: error.blockers,
          };
        }
        const message = error instanceof Error ? error.message : 'Submission failed.';
        return { ok: false, kind: 'refused', reason: 'validation-failed', message };
      } finally {
        // Always released, so a failure does not leave the release permanently locked
        // out of a retry.
        endSubmit(releaseId);
      }
    },
    [beginSubmit, endSubmit, addEvent, updateRelease, unlimited]
  );

  const dashboard = useMemo(
    () =>
      buildPublisherDashboard({
        releases: visibleReleases,
        events: visibleEvents,
        platforms: visiblePlatforms,
      }),
    [visibleReleases, visibleEvents, visiblePlatforms]
  );

  const mostRecentDraftProgress = selectMostRecentDraftProgress(visibleReleases, wizardProgress);
  const fallbackDraftId = mostRecentDraftProgress?.[0] ?? null;
  const lastVisibleSurfaceTarget = (() => {
    if (!lastVisibleSurface) return null;
    if (lastVisibleSurface.kind === 'dashboard') return lastVisibleSurface;
    if (lastVisibleSurface.kind === 'wizard') {
      if (lastVisibleSurface.releaseId === null) return lastVisibleSurface;
      const release = selectReleaseById(visibleReleases, lastVisibleSurface.releaseId);
      if (!release) {
        return fallbackDraftId
          ? ({ kind: 'wizard', releaseId: fallbackDraftId } satisfies PublisherLastVisibleSurface)
          : ({ kind: 'dashboard' } satisfies PublisherLastVisibleSurface);
      }
      return release.externalId == null
        ? lastVisibleSurface
        : ({ kind: 'detail', releaseId: release.id } satisfies PublisherLastVisibleSurface);
    }
    return selectReleaseById(visibleReleases, lastVisibleSurface.releaseId)
      ? lastVisibleSurface
      : fallbackDraftId
        ? ({ kind: 'wizard', releaseId: fallbackDraftId } satisfies PublisherLastVisibleSurface)
        : ({ kind: 'dashboard' } satisfies PublisherLastVisibleSurface);
  })();

  return {
    releases: visibleReleases,
    payments: visiblePayments,
    events: visibleEvents,
    platforms: visiblePlatforms,
    pendingDashboardDeletions,
    pendingReleaseDeletions,
    counts: countReleases(visibleReleases),
    /** Grouped sections and overview figures for the dashboard home. */
    dashboard,
    /** True when the account holds the Publisher plan. UX gating only — see §5. */
    entitled,
    /** Where the backend hydration has got to. */
    load,
    /** Release ids with a submission in flight. */
    submitting,

    createRelease,
    updateRelease,
    scheduleReleaseDeletion,
    undoReleaseDeletion,
    setStatus,
    addEvent,
    ensurePayment,
    payFee,
    submit,
    simulateReleasedRelease,
    setWizardProgress,
    clearWizardProgress,
    setLastVisibleSurface,
    loadPublisher,

    /** The Publisher screen that should reopen when the user re-enters, if any. */
    lastVisibleSurface: lastVisibleSurfaceTarget,
    /** The most recently touched editable draft and where it stopped, or null. */
    mostRecentDraftProgress,
    /** One release's saved wizard position, if any. */
    wizardProgressFor: (releaseId: string) => wizardProgress[releaseId] ?? null,

    /** The payment record for a release, or null. */
    paymentFor: (releaseId: string) =>
      visiblePayments.find((p) => p.releaseId === releaseId) ?? null,
    /** The stored per-outlet rows for one release. */
    platformRowsFor: (releaseId: string) =>
      visibleReleaseIds.has(releaseId) ? platformRowsForRaw(releaseId) : [],
    /** This release's events, oldest first. */
    eventsFor: (releaseId: string) =>
      visibleEvents
        .filter((e) => e.releaseId === releaseId)
        .sort((a, b) => a.at.localeCompare(b.at)),
    /** Lyrixa's own completeness issues for a release. NOT the distributor's verdict. */
    issuesFor: (release: PublisherRelease) => findPublisherIssues({ release, songs, songsLoaded }),
    /** Whether Lyrixa's own checks pass. Submission needs more than this. */
    isComplete: (release: PublisherRelease) => isReleaseComplete({ release, songs, songsLoaded }),
  };
}
